import { useMutation } from 'react-query';
import { Button, Grid, Stack, Typography } from '@mui/material';
import { SimpleForm } from 'components/forms/SimpleForm';
import { MultiSelectInput } from 'components/inputs/MultiSelectInput';
import { MultilineTextInput } from 'components/inputs/MultilineTextInput';
import { TextInput } from 'components/inputs/TextInput';
import Panel from 'components/panels/Panel';
import { addMinutes } from 'date-fns';
import { useAxios } from 'hooks/useAxios';
import { useSnackbar } from 'notistack';
import { keysToSnakeCase } from 'utils/formatters';
import * as yup from 'yup';

const SEVERITY_OPTIONS = ['error', 'warning', 'info'];
const TYPE_OPTIONS = ['notification', 'command response'];

export const Component = () => {
  const { post } = useAxios();
  const { enqueueSnackbar } = useSnackbar();

  const schema = yup.object().shape({
    unitId: yup.string().required(),
    severity: yup.array().of(yup.string().oneOf(SEVERITY_OPTIONS)).min(1).required(),
    type: yup.array().of(yup.string().oneOf(TYPE_OPTIONS)).min(1).required(),
    message: yup.string(),
    code: yup.string().required(),
    frequency: yup.string().required(),
    from: yup.string().required(),
    to: yup.string().required(),
    duration: yup.string().required(),
  });

  const defaultValues = {
    unitId: [],
    severity: ['error'],
    message: 'This is a test event.',
    type: ['notification'],
    code: '123',
    frequency: '30S',
    duration: '5M',
    from: new Date().toISOString(),
    to: addMinutes(new Date(), 2).toISOString(),
  };

  const createMutation = (endpoint: string) =>
    useMutation((payload: Record<string, any>) => post(endpoint, keysToSnakeCase(payload) as Record<string, unknown>), {
      onSuccess: () => {
        enqueueSnackbar('Successfully generated event(s)', { variant: 'success' });
      },
      onError: () => enqueueSnackbar('Error', { variant: 'error' }),
    });

  const singleEventMutation = createMutation('/test-data/generate-event/');
  const multipleEventsMutation = createMutation('/test-data/batch-generate-events/');
  const liveEventsMutation = createMutation('/test-data/generate-live-events/');

  const createEventConfigsPayload = (formValues) => ({
    ...formValues,
    unitId: formValues.unitId
      ?.split(',')
      .map((value: string) => value.trim())
      .filter(Boolean),
    code: formValues.code
      ?.split(',')
      .map((value) => value.trim())
      .filter(Boolean)
      .map(Number),
    message: [formValues.message],
  });

  const generateSingleEvent = (data) => {
    const payload = createEventConfigsPayload(data);
    singleEventMutation.mutate(payload);
  };

  const generateMultipleEvents = (data) => {
    const eventConfigs = createEventConfigsPayload(data);
    const payload = {
      eventDataOptions: eventConfigs,
      tsFrom: data.from,
      tsTo: data.to,
      frequency: `PT${data.frequency}`,
    };
    multipleEventsMutation.mutate(payload);
  };

  const generateLiveEvents = (data) => {
    const eventConfigs = createEventConfigsPayload(data);
    const payload = {
      eventDataOptions: eventConfigs,
      frequency: `PT${data.frequency}`,
      duration: `PT${data.duration}`,
    };
    liveEventsMutation.mutate(payload);
  };

  return (
    <>
      <Typography variant="h4">Generate test data for staging environment</Typography>
      <Typography variant="h5">Generate events</Typography>
      <SimpleForm defaultValues={defaultValues} validationSchema={schema} showButtons={false} onSubmit={() => {}}>
        {({ control, handleSubmit, formState, setValue, watch }) => {
          return (
            <Grid container spacing={3} sx={{ pr: 3, mt: 2 }}>
              <Grid item xs={12}>
                <Panel title="Event configuration">
                  <Grid container item spacing={3}>
                    <Grid item xs={12}>
                      <TextInput
                        control={control}
                        name="unitId"
                        label="Asset external identifier(s) *"
                        gridSize={12}
                        placeholder="GREENER_001, GREENER_002, ..."
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <MultiSelectInput
                        title="Severity(ies) *"
                        options={SEVERITY_OPTIONS.map((option) => ({ id: option, title: option }))}
                        selectedOptions={watch('severity')}
                        onChange={(value) => setValue('severity', value, { shouldValidate: true })}
                        sx={{ width: '100%' }}
                        isError={!!formState.errors.severity}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <MultiSelectInput
                        title="Type(s) *"
                        options={TYPE_OPTIONS.map((option) => ({ id: option, title: option }))}
                        selectedOptions={watch('type')}
                        onChange={(value) => setValue('type', value, { shouldValidate: true })}
                        sx={{ width: '100%' }}
                        isError={!!formState.errors.type}
                      />
                    </Grid>
                    <TextInput control={control} name="code" label="Code(s) *" placeholder="123, 44, ..." />
                    <MultilineTextInput control={control} name="message" label="Message" rows={2} gridSize={12} />
                  </Grid>
                </Panel>
              </Grid>
              <Grid item xs={12} md={4}>
                <Panel title="Single event">
                  <Stack direction="column" gap={2}>
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={handleSubmit(generateSingleEvent)}
                      disabled={!formState.isValid}
                    >
                      Generate single event
                    </Button>
                  </Stack>
                </Panel>
              </Grid>
              <Grid container item xs={12} md={4}>
                <Panel title="Multiple events">
                  <Stack direction="column" gap={2}>
                    <TextInput control={control} name="frequency" label="Frequency *" gridSize={12} />
                    <TextInput control={control} name="from" label="From *" gridSize={12} />
                    <TextInput control={control} name="to" label="To *" gridSize={12} />
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={handleSubmit(generateMultipleEvents)}
                      disabled={!formState.isValid}
                    >
                      Generate multiple events
                    </Button>
                  </Stack>
                </Panel>
              </Grid>
              <Grid item xs={12} md={4}>
                <Panel title="Live events">
                  <Stack direction="column" gap={2}>
                    <TextInput control={control} name="frequency" label="Frequency *" gridSize={12} />
                    <TextInput control={control} name="duration" label="Duration *" gridSize={12} />
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={handleSubmit(generateLiveEvents)}
                      disabled={!formState.isValid}
                    >
                      Generate live events
                    </Button>
                  </Stack>
                </Panel>
              </Grid>
            </Grid>
          );
        }}
      </SimpleForm>
    </>
  );
};

Component.displayName = 'TestDataGenerator';
